/**
 * Normalizador central de handle do Instagram. Pura, sem I/O, sem
 * dependências externas — usada tanto no backend (validação ao salvar)
 * quanto no frontend (exibição de dados antigos + prévia no formulário).
 *
 * Nunca tenta "consertar" um username errado — só interpreta formatos
 * equivalentes de representar o MESMO valor (com/sem @, com/sem URL,
 * com/sem query string). Qualquer coisa fora disso retorna null.
 */

export interface InstagramHandle {
  username: string;
  display: string;
  url: string;
}

const USERNAME_RE = /^[A-Za-z0-9_.]{1,30}$/;

// Segmentos de path do Instagram que NUNCA são um username de perfil —
// são rotas do próprio site (post, reel, story, explorar, etc.).
const RESERVED_PATH_SEGMENTS = new Set([
  'p', 'reel', 'reels', 'tv', 'stories', 'story',
  'explore', 'accounts', 'direct', 'about', 'developer', 'legal', 'privacy',
]);

const DANGEROUS_SCHEME_RE = /^(javascript|data|vbscript|file):/i;

export function normalizeInstagram(raw: unknown): InstagramHandle | null {
  if (typeof raw !== 'string') return null;

  let value = raw.trim();
  if (!value) return null;

  if (DANGEROUS_SCHEME_RE.test(value)) return null;

  // Remove um único @ inicial — cobre tanto "@usuario" quanto o formato
  // legado quebrado "@https://www.instagram.com/usuario/".
  if (value.startsWith('@')) value = value.slice(1);
  value = value.trim();
  if (!value) return null;

  if (DANGEROUS_SCHEME_RE.test(value)) return null;

  const hasInstagramDomain = /instagram\.com/i.test(value);
  const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
  const startsWithWww = /^www\./i.test(value);
  const looksLikeUrlOrDomain = hasInstagramDomain || hasProtocol || startsWithWww;

  let username: string;

  if (looksLikeUrlOrDomain) {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    let parsed: URL;
    try {
      parsed = new URL(withProtocol);
    } catch {
      return null;
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;

    const host = parsed.hostname.toLowerCase();
    if (host !== 'instagram.com' && host !== 'www.instagram.com') return null;

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return null; // instagram.com sozinho, sem perfil

    const first = segments[0];
    if (RESERVED_PATH_SEGMENTS.has(first.toLowerCase())) return null;

    username = first;
  } else {
    // Não tem nenhuma pista de URL/domínio — trata o valor inteiro como
    // um possível username cru. A validação final abaixo é quem decide.
    username = value;
  }

  if (!USERNAME_RE.test(username)) return null;

  return {
    username,
    display: `@${username}`,
    url: `https://www.instagram.com/${username}/`,
  };
}
